import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import {
    MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
    MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, last, takeUntil } from 'rxjs/operators';

// CONFIGS
import { trackByValue } from '../../../shared/util/track-by.util';
import { ETrackstarEnvironment } from '../../../system-setting/constants';

import { SnackBarConfig } from './../../../shared/configs/snackbar.config';

// INTERFACES
import { IAppIntegrationAccount } from './../../../shared/interfaces/app-integration-account.interface';

// SERVICES
import { AppIntegrationAccountApiService } from './../../../shared/services/api/app-integration/app-integration-account-api.service';
import { TrackstarApiService } from './../../../shared/services/api/app-integration/trackstar-api.service';
import { HubSpotApiService } from './../../../shared/services/api/app-integration/hubspot-api.service';

// UTILS

@Component({
  selector: 'lum-app-integration-account-form',
  templateUrl: './app-integration-account-form.component.html',
  styleUrls: ['./app-integration-account-form.component.scss'],
})
export class AppIntegrationAccountFormComponent implements OnInit, OnDestroy {
  form :UntypedFormGroup;

  rules$ = new BehaviorSubject<any>(<any>{});
  loadingRules$ = new BehaviorSubject<boolean>(<boolean>false);
  loadingTrackstarEnvironment$ = new BehaviorSubject<boolean>(<boolean>false);

  selectedApp :string;
  trackstarConfig :any = {};
  hubspotConfig :any = {};
  fieldMetadata :{ [key :string] :any } = {};
  currentActions :any = {};

  trackByValue = trackByValue;

  private onDestroy$ = new Subject();

  constructor(
    private fb :UntypedFormBuilder,
    private appIntegrationAccountApiService :AppIntegrationAccountApiService,
    private trackstarApiService :TrackstarApiService,
    private hubspotApiService :HubSpotApiService,
    private snackBar :MatSnackBar,
    private dialogRef :MatDialogRef<AppIntegrationAccountFormComponent>,
    @Inject(MAT_DIALOG_DATA)
    private data :{
      account :IAppIntegrationAccount;
    },
  ) {}

  ngOnInit() :void {
    this.getRules();
    this.initForm();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSelectAppType(event :Event) :void {
    const selectedOption = (event.target as HTMLSelectElement).value;
    this.selectedApp = selectedOption;
    this.updateFormFields({
      appName: selectedOption,
    });

    this.updateActions(selectedOption); // Call updateActions here

    // TODO: This is a temporary solution to handle the trackstar app type. This should be dynamic in the future.
    this.loadTrackstarConfig(selectedOption);
  }

  private loadTrackstarConfig(selectedOption: string|null = null): void {
    if (!selectedOption) {
      return;
    }

    if (selectedOption === 'trackstar') {
      // Get rule from rules using selectdOption
      this.initTrackstarConfig(
        this.rules[selectedOption].environment === ETrackstarEnvironment.SANDBOX,
      );
    }

    // Initialize HubSpot config when HubSpot is selected
    if (selectedOption === 'hubspot') {
      this.initHubSpotConfig();
    }
  }

  onTestCredentials() :void {
    // For backward compatibility, delegate to the new handler
    this.handleTestCredentials();
  }

  getActionKeys(actions :any) :string[] {
    return actions ? Object.keys(actions) : [];
  }

  handleActionClick(action :any, actionKey :string) :void {
    // Delegate to the appropriate handler based on action type
    switch (actionKey) {
      case 'save':
        this.onSubmit();
        break;
      case 'testCredentials':
        this.handleTestCredentials();
        break;
      case 'authorize':
        this.handleAuthorize();
        break;
      default:
        // Handle other actions if needed
        break;
    }
  }

  /**
   * Handle testing credentials for different integration types
   */
  private handleTestCredentials() :void {
    this.currentActions['testCredentials'].loading = true;

    const credentials = this.form.get('credentials').value;
    credentials.appIntegrationAccountId = this.data?.account?.id ?? null;

    this.appIntegrationAccountApiService
      .testOmsCredentials(this.selectedApp, credentials)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.currentActions['testCredentials'].loading = false)),
      )
      .subscribe({
        next: (response) => {
          this.snackBar.open('Credentials are valid', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (error) => {
          this.snackBar.open(
            error.message || 'Failed to validate credentials',
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  /**
   * Handle authorization for different integration types
   */
  private handleAuthorize() :void {
    this.currentActions['authorize'].loading = true;

    // OAuth authorization flow
    if (this.authType === 'OAUTH') {
      this.handleOAuthAuthorization();
    } else {
      // For non-OAuth authorizations
      this.currentActions['authorize'].loading = false;
      this.snackBar.open(
        'Authorization not supported for this integration type',
        'Fail',
        {
          duration: SnackBarConfig.DEFAULT.duration,
        },
      );
    }
  }

  /**
   * Handle OAuth authorization flow
   */
  private handleOAuthAuthorization() :void {
    const saveAndAuthorize = () => {
      this.appIntegrationAccountApiService
        .getOAuthAuthorizationUrl(this.data.account.id)
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => (this.currentActions['authorize'].loading = false)),
        )
        .subscribe({
          next: (response) => {
            window.location.href = response.url;
          },
          error: (error) => {
            this.snackBar.open(
              error.message || 'Failed to get authorization URL',
              'Fail',
              {
                duration: SnackBarConfig.DEFAULT.duration,
              },
            );
          },
        });
    };

    if (this.mode === 'edit' && this.data.account.id) {
      // If editing an existing account, just get the auth URL
      saveAndAuthorize();
    } else {
      // If creating a new account, save it first then authorize
      this.appIntegrationAccountApiService
        .create({
          ...this.form.value,
          authType: this.authType,
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response) => {
            this.data.account = response;
            saveAndAuthorize();
          },
          error: (responseError) => {
            this.currentActions['authorize'].loading = false;
            this.snackBar.open(
              responseError?.error ? responseError.error : 'Error creating account',
              'Fail',
              {
                duration: SnackBarConfig.DEFAULT.duration,
              },
            );
          },
        });
    }
  }

  // TODO: Create pipe
  isReadOnly(controlName :string, key :string) :boolean {
    const appConfig = this.rules[this.form.get('app')?.value];

    return (
      appConfig &&
      appConfig.fields[key][controlName] &&
      appConfig.fields[key][controlName]?.attributes?.includes('readonly')
    );
  }

  onSubmit() :void {
    this.currentActions['save'].loading = true;

    if (this.mode === 'edit') {
      this.appIntegrationAccountApiService
        .update(this.data.account.id, {
          ...this.form.value,
          authType: this.authType,
        })
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => (this.currentActions['save'].loading = false)),
        )
        .subscribe({
          next: (response) => {
            this.snackBar.open('Account updated', 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });

            this.dialogRef.close(response);
          },
          error: (responseError) => {
            this.snackBar.open(
              responseError?.error ? responseError.error : 'Error updating record',
              'Fail',
              {
                duration: SnackBarConfig.DEFAULT.duration,
              },
            );
          },
        });
    } else {
      this.appIntegrationAccountApiService
        .create({
          ...this.form.value,
          authType: this.authType,
        })
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => (this.currentActions['save'].loading = false)),
        )
        .subscribe({
          next: (response) => {
            this.snackBar.open('Account created', 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });

            this.dialogRef.close(response);
          },
          error: (responseError) => {
            this.snackBar.open(
              responseError?.error ? responseError.error : 'Error creating record',
              'Fail',
              {
                duration: SnackBarConfig.DEFAULT.duration,
              },
            );
          },
        });
    }
  }

  private getDefaultValue(rule :string) :any {
    if (rule.includes('boolean')) {
      return false; // Default value for boolean
    } else if (rule.includes('string')) {
      return ''; // Default value for string
    }
    // Add more conditions for other types as needed
    return null; // Fallback default value
  }

  private updateFormFields(data :{
    appName :string;
    credentials ?:any;
    settings ?:any;
  }) :void {
    this.updateCredentialsFormFields(data);
    this.updateSettingsFormFields(data);

    this.form.updateValueAndValidity();
  }

  private updateCredentialsFormFields(data :{
    appName :string;
    credentials ?:any;
  }) :void {
    const { appName, credentials } = data;
    // Access the credentials FormGroup
    const credentialsGroup = this.form.get('credentials') as UntypedFormGroup;

    // Clear existing dynamic controls
    Object.keys(credentialsGroup.controls).forEach((key) => {
      credentialsGroup.removeControl(key);
    });

    const appConfig = this.rules[appName];
    if (appConfig && appConfig.rules && appConfig.fields?.credentials) {
      const ruleCredentialConfig = appConfig.rules?.credentials;
      const fieldCredentialConfig = appConfig.fields?.credentials;

      Object.keys(ruleCredentialConfig).forEach((key) => {
        const validators = this.getValidatorsForConfig(ruleCredentialConfig[key]);
        let initialValue =
          credentials && credentials[key] ? credentials[key] : null;

        if (!initialValue) {
          initialValue = this.getDefaultValue(ruleCredentialConfig[key]);
        }

        credentialsGroup.addControl(key, this.fb.control(initialValue, validators));
        // Store additional field metadata for rendering
        this.fieldMetadata[key] = {
          type: fieldCredentialConfig[key].type,
          label: fieldCredentialConfig[key].label,
          values: fieldCredentialConfig[key].values,
          attributes: fieldCredentialConfig[key].attributes,
        };
      });
    }
  }

  private updateSettingsFormFields(data :{
    appName :string;
    settings ?:any;
  }) :void {
    const { appName, settings } = data;
    // Access the settings FormGroup
    const settingsGroup = this.form.get('settings') as UntypedFormGroup;

    // Clear existing dynamic controls
    Object.keys(settingsGroup.controls).forEach((key) => {
      settingsGroup.removeControl(key);
    });

    const appConfig = this.rules[appName];
    if (appConfig && appConfig.rules && appConfig.fields?.settings) {
      const ruleSettingsConfig = appConfig.rules?.settings;
      const fieldSettingsConfig = appConfig.fields?.settings;

      Object.keys(ruleSettingsConfig).forEach((key) => {
        const validators = this.getValidatorsForConfig(ruleSettingsConfig[key]);
        let initialValue = settings && settings[key] ? settings[key] : null;

        if (!initialValue) {
          initialValue = this.getDefaultValue(ruleSettingsConfig[key]);
        }

        switch (fieldSettingsConfig[key].type) {
          case 'CHECKBOX':
            // check if initialValue is a string
            if (typeof initialValue === 'string') {
              initialValue = initialValue === 'true';
            }
            break;
          default:
            break;
        }

        settingsGroup.addControl(key, this.fb.control(initialValue, validators));

        // Store additional field metadata for rendering
        this.fieldMetadata[key] = {
          type: fieldSettingsConfig[key].type,
          label: fieldSettingsConfig[key].label,
          values: fieldSettingsConfig[key].values,
          attributes: fieldSettingsConfig[key].attributes,
        };
      });
    }
  }

  private getValidatorsForConfig(configString :string) :ValidatorFn[] {
    const validators :ValidatorFn[] = [];
    const configParts = configString.split('|');

    for (const part of configParts) {
      switch (part) {
        case 'required':
          validators.push(Validators.required);
          break;
        // Add cases for other types of validation if needed
      }
    }

    return validators;
  }

  private getRules() :void {
    this.loadingRules$.next(true);
    this.appIntegrationAccountApiService
      .getRules()
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.loadingRules$.next(false)),
      )
      .subscribe({
        next: (response) => {
          this.rules$.next(response);
          if (this.data?.account) {
            this.initForm();
            this.selectedApp = this.data.account.app;
            this.updateActions(this.data.account.app);
          }
        },
        error: (err) => {
          this.snackBar.open('Error getting rules', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private initForm() :void {
    this.form = this.fb.group({
      label: [this.data?.account?.label || null, Validators.required],
      app: [this.data?.account?.app || null, Validators.required],
      credentials: this.fb.group({}),
      settings: this.fb.group({}),
    });

    if (this.data?.account && Object.keys(this.rules ?? {}).length) {
      this.selectedApp = this.data.account.app;
      this.updateFormFields({
        appName: this.data.account.app,
        credentials: this.data.account.credentials,
        settings: this.data.account.settings,
      });
      this.loadTrackstarConfig(this.data.account.app);
    }
  }

  private initTrackstarConfig(sandbox :boolean) :void {
    this.trackstarConfig = {
      sandbox,
      getLinkToken: () => {
        return this.trackstarApiService
          .getLinkToken()
          .pipe(last())
          .toPromise()
          .then((data) => {
            const { link_token: linkToken } = data;

            return linkToken;
          })
          .catch((err) => {
            this.snackBar.open(
              err?.error ? err.error : 'Invalid API Key provided',
              'Close',
              {
                duration: SnackBarConfig.DEFAULT.duration,
              },
            );
            throw err;
          });
      },
      onSuccess: async (authCode :any) => {
        // call API to get Access Token
        this.getAccessToken({
          api_key: this.credentials.get('api_key')?.value,
          auth_code: authCode,
        });
      },
    };
  }

  private getAccessToken(credentials :{
    api_key :string;
    auth_code :string;
  }) :void {
    this.trackstarApiService
      .getAccessToken(credentials)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (data) => {
          const { access_token } = data;
          // Update form credentials with access token
          this.credentials.patchValue({
            access_token,
          });
        },
        error: (err) => {},
      });
  }

  private updateActions(appName :string) :void {
    if (this.rules[appName] && this.rules[appName].actions) {
      this.currentActions = this.rules[appName].actions;
    } else {
      this.currentActions = {};
    }
  }

  get rules() :any {
    return this.rules$.getValue();
  }

  get currentRule() :any {
    return this.rules[this.selectedApp];
  }

  get loadingRules() :boolean {
    return this.loadingRules$.getValue();
  }

  get apps() :string[] {
    return Object.keys(this.rules);
  }

  // Get auth type from rules and selected app
  get authType() :string {
    const appConfig = this.rules[this.selectedApp];

    return appConfig ? appConfig.authType : null;
  }

  get dynamicCredentialsFormControls() :string[] {
    const credentialsGroup = this.form.get('credentials') as UntypedFormGroup;

    return credentialsGroup ? Object.keys(credentialsGroup.controls) : [];
  }

  get dynamicSettingsFormControls() :string[] {
    const settingsGroup = this.form.get('settings') as UntypedFormGroup;

    return settingsGroup ? Object.keys(settingsGroup.controls) : [];
  }

  get mode() :string {
    return this.data.account ? 'edit' : 'create';
  }

  get appControlValue() :string {
    return this.form.get('app')?.value;
  }

  get credentials() :UntypedFormGroup {
    return this.form.get('credentials') as UntypedFormGroup;
  }

  get loadingTrackstarEnvironment() :boolean {
    return this.loadingTrackstarEnvironment$.getValue();
  }

  private initHubSpotConfig() :void {
    this.hubspotConfig = {
      getAuthUrl: () => {
        // First, check if we have an existing account or need to create one
        if (this.data?.account?.id) {
          // Use existing account ID
          return this.hubspotApiService
            .getAuthorizationUrl(this.data.account.id)
            .pipe(last())
            .toPromise()
            .then((data) => {
              // Handle nested response structure: data.url.url
              return data.url?.url || data.url;
            })
            .catch((err) => {
              this.snackBar.open(
                err?.error ? err.error : 'Failed to get HubSpot authorization URL',
                'Close',
                {
                  duration: SnackBarConfig.DEFAULT.duration,
                },
              );
              throw err;
            });
        } else {
          // Need to create account first, then get auth URL
          return this.appIntegrationAccountApiService
            .create({
              ...this.form.value,
              authType: 'OAUTH',
            })
            .pipe(
              takeUntil(this.onDestroy$),
              last()
            )
            .toPromise()
            .then((createdAccount) => {
              // Update the data with the newly created account
              this.data.account = createdAccount;
              
              // Now get the authorization URL
              return this.hubspotApiService
                .getAuthorizationUrl(createdAccount.id)
                .pipe(last())
                .toPromise()
                .then((data) => {
                  // Handle nested response structure: data.url.url
                  return data.url?.url || data.url;
                });
            })
            .catch((err) => {
              this.snackBar.open(
                err?.error ? err.error : 'Failed to create account or get authorization URL',
                'Close',
                {
                  duration: SnackBarConfig.DEFAULT.duration,
                },
              );
              throw err;
            });
        }
      },
      onSuccess: (authCode :string) => {
        // HubSpot handles the callback differently than Trackstar
        // The success would typically be handled by a redirect callback
        this.snackBar.open('HubSpot authorization initiated', 'Success', {
          duration: SnackBarConfig.DEFAULT.duration,
        });
      },
      onLoad: () => {
        // Optional callback when the authorization process starts
        console.log('HubSpot authorization process started');
      },
      onClose: () => {
        // Optional callback when the authorization window is closed
        console.log('HubSpot authorization window closed');
      },
    };
  }
}
